// /opt/isp_erp/frontend/src/pages/admin/clientes/components/ReactivarClienteDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Box, Typography, TextField,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Divider, Chip, Paper, Grid
} from "@mui/material";
import axios from "../../../../api/axios";

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.error) return d.error;
  if (Array.isArray(d)) return d.map((x) => x.msg).join("; ");
  return porDefecto;
};

const ReactivarClienteDialog = ({ cliente, onCerrar, onHecho }) => {
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [tecnicoId, setTecnicoId] = useState("");
  const [direccion, setDireccion] = useState(cliente.direccion || "");
  const [descripcion, setDescripcion] = useState("");

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const r = await axios.get(`/customers/${cliente.id}/reactivacion`);
        if (!vigente) return;
        setVista(r.data);
        if (r.data.modo === "REINSTALACION") {
          const u = await axios.get("/users");
          if (vigente) {
            setTecnicos((u.data || []).filter((x) => x.roles?.some((role) => role.name === "tecnico_campo")));
          }
        }
      } catch (err) {
        if (vigente) setError(detalleError(err, "No se pudo consultar la reactivación del cliente"));
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => {
      vigente = false;
    };
  }, [cliente.id]);

  const reactivar = async () => {
    if (vista.modo === "REINSTALACION" && !tecnicoId) {
      setError("Selecciona el técnico que hará la reinstalación");
      return;
    }
    setEnviando(true);
    setError("");
    try {
      const cuerpo = { modo: vista.modo, motivo: motivo.trim() || null };
      if (vista.modo === "REINSTALACION") {
        cuerpo.tecnico_id = tecnicoId;
        if (direccion.trim() && direccion.trim() !== (cliente.direccion || "")) cuerpo.direccion = direccion.trim();
        if (descripcion.trim()) cuerpo.descripcion = descripcion.trim();
      }
      const r = await axios.post(`/customers/${cliente.id}/reactivar`, cuerpo);
      setResultado(r.data);
    } catch (err) {
      setError(detalleError(err, "No se pudo reactivar al cliente"));
    } finally {
      setEnviando(false);
    }
  };

  const datos = vista?.datos_actuales || {};
  const nombre = vista?.nombre || `${cliente.nombres} ${cliente.apellidos}`;

  return (
    <Dialog open onClose={() => !enviando && (resultado ? onHecho() : onCerrar())} maxWidth="sm" fullWidth>
      <DialogTitle>Reactivar cliente</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" fontWeight="bold">{nombre}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {vista?.identificacion ? `Identificación ${vista.identificacion} · ` : ""}Estado actual: {vista?.estado || cliente.estado}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {cargando && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!cargando && vista && !vista.puede_reactivar && (
          <Alert severity="warning">{vista.motivo_bloqueo}</Alert>
        )}

        {!cargando && vista && vista.puede_reactivar && !resultado && vista.modo === "CANCELAR_RETIRO" && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              El retiro todavía no se ha cerrado, así que el cliente se reactiva con <strong>los mismos datos</strong>.
              Se reanuda su servicio en la OLT y se cancelan las órdenes de retiro abiertas.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "#fafafa" }}>
              <Grid container spacing={1}>
                <Grid item xs={4}><Typography variant="caption" color="text.secondary">ONU</Typography><Typography variant="body2">{datos.onu_id ? `N.º ${datos.onu_id}` : "—"}</Typography></Grid>
                <Grid item xs={4}><Typography variant="caption" color="text.secondary">Puerto NAP</Typography><Typography variant="body2">{datos.puerto_nap ? `NAP ${datos.nap_id} · puerto ${datos.puerto_nap}` : "—"}</Typography></Grid>
                <Grid item xs={4}><Typography variant="caption" color="text.secondary">IP</Typography><Typography variant="body2">{datos.ip_asignada || "Sin IP"}</Typography></Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2">
                Órdenes de retiro abiertas que se cancelarán:{" "}
                {vista.retiros_abiertos.length === 0
                  ? "ninguna"
                  : vista.retiros_abiertos.map((t) => (
                      <Chip key={t.id} size="small" label={`${t.ticket_number} (${t.status})`} sx={{ ml: 0.5 }} />
                    ))}
              </Typography>
              {vista.motivo_retiro && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Motivo del retiro: {vista.motivo_retiro}
                </Typography>
              )}
            </Paper>
            <TextField fullWidth size="small" multiline rows={2} label="Motivo de la reactivación (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </>
        )}

        {!cargando && vista && vista.puede_reactivar && !resultado && vista.modo === "REINSTALACION" && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Los equipos ya fueron retirados. Se creará una <strong>reinstalación</strong> para este cliente: una instalación
              aprobada y un ticket para el técnico. El cliente queda <strong>pendiente de instalación</strong> y pasa a ACTIVO
              cuando se cierre ese ticket. La ONU, el puerto de NAP y la IP anteriores quedan libres.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Técnico *</InputLabel>
                  <Select label="Técnico *" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
                    {tecnicos.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.username || t.nombre || t.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Dirección de la instalación" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Motivo de la reactivación (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Observaciones para el técnico (opcional)" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
              </Grid>
            </Grid>
          </>
        )}

        {resultado && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>{resultado.message}</Alert>
            {resultado.modo === "REINSTALACION" && (
              <Typography variant="body2">
                Ticket <strong>{resultado.ticket_number}</strong> asignado a <strong>{resultado.tecnico}</strong>.
                Siguiente paso: en <em>Instalaciones &gt; Aprobadas</em>, usa "Dar de alta" en la instalación N.º {resultado.installation_id}.
              </Typography>
            )}
            {resultado.modo === "CANCELAR_RETIRO" && resultado.tickets_cancelados?.length > 0 && (
              <Typography variant="body2">Órdenes canceladas: {resultado.tickets_cancelados.join(", ")}</Typography>
            )}
            {(resultado.advertencias || []).map((a, i) => (
              <Alert key={i} severity="warning" sx={{ mt: 1 }}>{a}</Alert>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {resultado ? (
          <Button variant="contained" onClick={onHecho}>Cerrar</Button>
        ) : (
          <>
            <Button onClick={onCerrar} disabled={enviando}>Cancelar</Button>
            <Button
              variant="contained"
              color="success"
              onClick={reactivar}
              disabled={enviando || cargando || !vista || !vista.puede_reactivar}
              startIcon={enviando ? <CircularProgress size={16} /> : null}
            >
              {vista?.modo === "REINSTALACION" ? "Crear reinstalación" : "Reactivar cliente"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReactivarClienteDialog;
